import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ModuleBase } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import classNames from 'classnames';

interface TopTenItem {
  title?: string;
  text?: string;
  reportUrl?: {
    text: string;
    href: string;
  };
  reportId?: string;
  searchTerm?: string;
}

type TopTenModuleModel = ModuleBaseModel<
  {
    heading?: string;
    popularReports?: TopTenItem[];
    popularSearches?: string[];
    showSearchedTab?: boolean;
  } & ModuleBasePropsModel
>;

const TopTenModule = (data: TopTenModuleModel) => {
  const { props } = data;
  const [activeTab, setActiveTab] = useState<'popular' | 'searched'>('popular');

  const mostPopular = props.popularReports || [];
  const mostSearched = useMemo(
    () =>
      (props.popularSearches || []).map((term, index) => ({
        text: term,
        searchTerm: term,
        reportUrl: { text: term, href: `/search?searchTerm=${encodeURIComponent(term.toLocaleLowerCase())}` },
        reportId: `search-${index}`,
      })) as TopTenItem[],
    [props.popularSearches],
  );

  const items = activeTab === 'popular' ? mostPopular : mostSearched;

  return (
    <ModuleBase data={data} noPadding noModuleBaseColor>
      <div className="TopTenModule container flex h-full flex-col">
        <div
          className="top-ten-content flex grow flex-col overflow-hidden border-l-2 border-[#EA5C6D] bg-white"
          style={{ borderBottomLeftRadius: '5px' }}
        >
          <div className="flex justify-around bg-[#AE2D2A]">
            <div className="flex justify-center">
              <button
                className="xxl:text-4xl relative flex cursor-pointer items-center justify-center border-none bg-transparent px-2 py-3 text-center text-xs font-light whitespace-nowrap text-white sm:px-3 sm:py-4 sm:text-sm md:px-4 md:py-5 md:text-base lg:px-3 lg:py-8 lg:text-[calc(2vw)]"
                onClick={() => setActiveTab('popular')}
              >
                Most Popular
                <span
                  className="absolute bottom-2 left-1/2 h-0.5 -translate-x-1/2 bg-white transition-all duration-300 ease-in-out sm:bottom-3 md:bottom-4 lg:bottom-5"
                  style={{ width: activeTab === 'popular' ? 'calc(100% - 25px)' : '0' }}
                />
              </button>
            </div>
            {props.showSearchedTab !== false && (
              <div className="flex justify-center">
                <button
                  className="xxl:text-4xl relative flex cursor-pointer items-center justify-center border-none bg-transparent px-2 py-3 text-center text-xs font-light whitespace-nowrap text-white sm:px-3 sm:py-4 sm:text-sm md:px-4 md:py-5 md:text-base lg:px-3 lg:py-8 lg:text-[calc(2vw)]"
                  onClick={() => setActiveTab('searched')}
                >
                  Most Searched
                  <span
                    className="absolute bottom-2 left-1/2 h-0.5 -translate-x-1/2 bg-white transition-all duration-300 ease-in-out sm:bottom-3 md:bottom-4 lg:bottom-5"
                    style={{ width: activeTab === 'searched' ? 'calc(100% - 25px)' : '0' }}
                  />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col">
            {items.map((item, index) => {
              const href = item.reportUrl?.href || '#';
              const displayText = item.title || item.text || item.searchTerm || item.reportUrl?.text;

              return (
                <Link
                  key={item.reportId || index}
                  href={href}
                  className="flex flex-1 cursor-pointer items-center border-b-2 border-white bg-[#EA5C6D] no-underline transition-colors hover:bg-[#D94B5D]"
                >
                  <div className="flex h-full w-[40px] min-w-[40px] items-center justify-center border-r-2 border-b-2 border-[#EA5C6D] bg-white py-[14px] text-xl font-bold text-[#EA5C6D] sm:w-[38px] sm:py-0 sm:text-2xl md:w-[60px] md:min-w-[60px] md:text-3xl lg:w-[70px] lg:min-w-[70px] lg:text-4xl xl:w-[80px] xl:min-w-[80px] xl:text-5xl">
                    {index + 1}
                  </div>
                  <div
                    className={classNames(
                      'flex flex-1 items-center px-2 py-3 text-xs leading-tight font-medium text-white sm:px-3 sm:py-4 sm:text-sm md:px-4 md:py-5 md:text-base lg:px-5 lg:py-6 lg:text-xs xl:text-xl',
                      activeTab === 'searched' && 'lowercase',
                    )}
                  >
                    {displayText}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default TopTenModule;
